import { db } from '../db/indexeddb'

/**
 * Per-entity "last pulled" timestamps the sync engine persists to Dexie settings
 * (see sync-pull `descriptor.settingsKey`). They live in the shared `settings`
 * table (no userId scoping), so any window — including the standalone preferences
 * window, which never runs the sync engine itself — can read them.
 */
export const SYNC_TIME_KEYS = [
  'lastSyncTime', // tasks
  'lastProjectSyncTime',
  'lastSectionSyncTime',
  'lastTagSyncTime',
  'lastGoalSyncTime',
  'lastHabitSyncTime',
  'lastHabitLogSyncTime',
] as const

/**
 * **Wann lief zuletzt ein Zyklus durch** — im Gegensatz zu den Schlüsseln oben,
 * die sagen, wie alt die zuletzt geholten Daten sind.
 *
 * Der Unterschied ist nicht akademisch. `sync-pull.ts` schreibt in
 * `lastXSyncTime` den höchsten `updated_at` der Cloud-Zeilen, nicht die
 * aktuelle Uhrzeit. Daraus folgen zwei Lügen in entgegengesetzte Richtungen:
 * Ein Zyklus, der sauber läuft und nichts Neues findet, lässt den Wert stehen
 * — die Anzeige altert, obwohl alles arbeitet. Und sobald irgendeine Zeile mit
 * frischem Serverstempel hereinkommt, wirkt sie taufrisch, auch wenn der Push
 * seit Stunden nichts mehr hochbekommt.
 *
 * Genau das war am 07.09.2026 der Fall: „vor 2 Minuten synchronisiert", während
 * 39 Notizen unten festsaßen. Die Anzeige hat die Fehlersuche zwei Mal in die
 * falsche Richtung geschickt.
 */
export const LAST_SYNC_RUN_KEY = 'lastSyncRunAt'

/** Hält fest, dass ein Zyklus eben durchgelaufen ist. */
export async function markSyncRun(at: Date = new Date()): Promise<void> {
  await db.setSetting(LAST_SYNC_RUN_KEY, at.toISOString())
}

/**
 * Wann zuletzt synchronisiert wurde.
 *
 * Bevorzugt den Zeitpunkt des letzten Laufs (`LAST_SYNC_RUN_KEY`). Fehlt der —
 * auf Geräten, die seit dem Update noch keinen Zyklus hatten —, bleibt der
 * Höchstwert der Datenstempel als beste verfügbare Auskunft. Falsch, aber
 * weniger falsch als „noch nie synchronisiert".
 */
export async function getLastSyncedAt(): Promise<Date | null> {
  const runIso = await db.getSettingValue<string | undefined>(LAST_SYNC_RUN_KEY)
  if (runIso) {
    const ms = new Date(runIso).getTime()
    if (!Number.isNaN(ms)) return new Date(ms)
  }

  let max: number | null = null
  for (const key of SYNC_TIME_KEYS) {
    const iso = await db.getSettingValue<string | undefined>(key)
    if (!iso) continue
    const ms = new Date(iso).getTime()
    if (!Number.isNaN(ms) && (max === null || ms > max)) max = ms
  }
  return max === null ? null : new Date(max)
}

/** A sync timestamp bucketed into a relative-time descriptor. */
export type RelativeSync =
  | { kind: 'justNow' }
  | { kind: 'minutes'; count: number }
  | { kind: 'hours'; count: number }
  | { kind: 'date'; date: Date }

/**
 * Bucket a sync timestamp relative to `now`. Pure — the caller passes `now` so it
 * stays testable and the component can re-render it on a timer.
 * - < 1 min  → just now
 * - < 60 min → N minutes ago
 * - < 24 h   → N hours ago
 * - else     → absolute date
 */
export function relativeSync(at: Date, now: Date): RelativeSync {
  const diffMs = Math.max(0, now.getTime() - at.getTime())
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return { kind: 'justNow' }
  if (minutes < 60) return { kind: 'minutes', count: minutes }
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return { kind: 'hours', count: hours }
  return { kind: 'date', date: at }
}
